import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-ink px-4 py-3.5 text-sm text-bone placeholder:text-fog/60 transition-colors duration-300 focus:border-mint focus:outline-none";

const EMPTY = { name: "", company: "", email: "", phone: "", package: "", message: "" };

export default function BookingForm({ packages, onSent, hideTitle = false }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email }));
  }, [user]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, lang });
      setSent(true);
      toast.success(t("booking.toast"));
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ ...EMPTY, name: user?.name || "", email: user?.email || "" });
    setSent(false);
  };

  if (sent) {
    return (
      <motion.div
        data-testid="booking-success-message"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="flex min-h-[420px] flex-col items-center justify-center text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-mint" />
        <h3 className="mt-6 font-display text-2xl font-bold text-bone">
          {t("booking.thanks")}, {form.name.split(" ")[0]}!
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-fog">{t("booking.success")}</p>
        <button
          data-testid="booking-another-button"
          onClick={reset}
          className="mt-8 text-sm font-semibold text-mint hover:underline"
        >
          {t("booking.another")}
        </button>
      </motion.div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit} className="space-y-5">
      {!hideTitle && <h3 className="font-display text-xl font-bold text-bone">{t("booking.formTitle")}</h3>}
      <div className="grid gap-5 sm:grid-cols-2">
        <input
          data-testid="booking-name-input"
          required
          value={form.name}
          onChange={update("name")}
          placeholder={`${t("booking.name")} *`}
          className={inputClass}
          aria-label={t("booking.name")}
        />
        <input
          data-testid="booking-company-input"
          value={form.company}
          onChange={update("company")}
          placeholder={t("booking.company")}
          className={inputClass}
          aria-label={t("booking.company")}
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <input
          data-testid="booking-email-input"
          required
          type="email"
          value={form.email}
          onChange={update("email")}
          placeholder={`${t("booking.emailPh")} *`}
          className={inputClass}
          aria-label={t("booking.emailPh")}
        />
        <input
          data-testid="booking-phone-input"
          value={form.phone}
          onChange={update("phone")}
          placeholder={t("booking.phone")}
          className={inputClass}
          aria-label={t("booking.phone")}
        />
      </div>
      <select
        data-testid="booking-package-select"
        value={form.package}
        onChange={update("package")}
        className={`${inputClass} appearance-none`}
        aria-label={t("booking.package")}
      >
        <option value="">{t("booking.package")}</option>
        {packages.map((p) => (
          <option key={p.name} value={p.name}>{`${p.name} – ${p.price}`}</option>
        ))}
        <option value="Osäker">{t("booking.unsure")}</option>
      </select>
      <textarea
        data-testid="booking-message-input"
        value={form.message}
        onChange={update("message")}
        placeholder={t("booking.message")}
        rows={4}
        className={`${inputClass} resize-none`}
        aria-label={t("booking.message")}
      />
      <button
        data-testid="booking-submit-button"
        type="submit"
        disabled={sending}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-mint px-6 py-4 text-sm font-bold text-ink transition-colors duration-300 hover:bg-bone disabled:opacity-60"
      >
        {sending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {t("booking.submit")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>
      <p className="text-center text-xs text-fog/70">{t("booking.gdpr")}</p>
    </form>
  );
}
